/**
 * ADR-0221 §3.5 — one issue (`/app/issues/:id`): `ActionItemDetail.tsx`
 * copied and trimmed. Meta pills (Initiative, status), the times line, the
 * edit card (Title, Description, the five-control row, the codes row by the
 * TARGET status, closure notes, the footer with explicit Save), tabs
 * Activity (comments + the hub-composed trail) and Action items. Save runs
 * the PATCH and, when the status changed, `/transition` in parallel, exactly
 * as the action-item page does; `missing_fields` maps to the highlighted
 * fields. The state machine is the hub's: the Status select offers only the
 * served `allowed_transitions`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { ActivityThread } from "../components/ActivityThread.js";
import type { MemberCandidate } from "../components/MemberPicker.js";
import { fmtDay, initiativeLabel } from "./TenantInitiatives.js";
import { STATUS_WORDS, statusPillClass, type IssueListItem, type IssueStatus } from "./TenantIssues.js";

// ── the served shapes (hub src/initiatives/issues/types.ts) ──────────────────

export type AllowedIssueTransition = { to: IssueStatus; requires: string[] };
export type Issue = IssueListItem & {
  description: string | null;
  /** id only for the Assigned to select; never rendered. */
  assignee_account_id: string | null;
  resolution_code_id: string | null;
  root_cause_code_id: string | null;
  dismissal_code_id: string | null;
  resolution_code_label: string | null;
  root_cause_code_label: string | null;
  dismissal_code_label: string | null;
  closure_notes: string | null;
  held_by: { operation_id: string; name: string; href: string | null } | null;
  resolved: { by: string; at: string } | null;
  closed: { by: string; at: string } | null;
  dismissed: { by: string; at: string } | null;
  allowed_transitions: AllowedIssueTransition[];
  can_edit: boolean;
  can_assign: boolean;
  can_comment: boolean;
  action_item_rows: Array<{ id: string; title: string; status: string; assignee: string | null; due_at: string | null; age_days: number }>;
  redacted: { by: string; at: string } | null;
  edited: { by: string; at: string } | null;
};
export type IssueActivityRow = { id: string; event_type: string; text: string; note: string | null; actor: { name: string } | null; actor_account_id: string | null; at: string; created_at: string };
export type IssueComment = { id: string; author: { name: string }; author_account_id: string; body: string; created_at: string; edited_at: string | null };
type Code = { id: string; label: string };

export const MISSING_FIELD_LABEL: Record<string, string> = {
  resolution_code_id: "Resolution code",
  root_cause_code_id: "Root cause code",
  dismissal_code_id: "Dismissal code",
  closure_notes: "Closure notes",
};

/** The Status select: the saved status first (selectable to revert), then only what the hub serves as legal for this person (ADR-0154 §6.3 C1). */
export function statusOptions(current: IssueStatus, allowed: readonly AllowedIssueTransition[]): Array<{ value: IssueStatus; label: string; disabled: boolean }> {
  return [{ value: current, label: STATUS_WORDS[current], disabled: allowed.length === 0 }, ...allowed.map((t) => ({ value: t.to, label: STATUS_WORDS[t.to], disabled: false }))];
}

/** What the hub will require for a move to `target`, read from the served contract. */
export function requiredFor(allowed: readonly AllowedIssueTransition[], target: IssueStatus): Set<string> {
  return new Set(allowed.find((t) => t.to === target)?.requires ?? []);
}

export interface EditForm {
  title: string;
  description: string;
  assignee_account_id: string;
  status: IssueStatus;
  team_group_id: string;
  held_by_operation_id: string;
  resolution_code_id: string;
  root_cause_code_id: string;
  dismissal_code_id: string;
  closure_notes: string;
}

export function issueToForm(x: Issue): EditForm {
  return {
    title: x.title,
    description: x.description ?? "",
    assignee_account_id: x.assignee_account_id ?? "",
    status: x.status,
    team_group_id: x.team?.id ?? "",
    held_by_operation_id: x.held_by?.operation_id ?? "",
    resolution_code_id: x.resolution_code_id ?? "",
    root_cause_code_id: x.root_cause_code_id ?? "",
    dismissal_code_id: x.dismissal_code_id ?? "",
    closure_notes: x.closure_notes ?? "",
  };
}

/** The PATCH body: only what changed, and closure notes only when the status is NOT changing (then they ride the transition). Pure, for the tests. */
export function patchBodyFor(form: EditForm, initial: EditForm): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (form.title !== initial.title) out.title = form.title.trim();
  if (form.description !== initial.description) out.description = form.description.trim() || null;
  if (form.team_group_id !== initial.team_group_id) out.team_group_id = form.team_group_id || null;
  if (form.held_by_operation_id !== initial.held_by_operation_id) out.held_by_operation_id = form.held_by_operation_id || null;
  if (form.status === initial.status && form.closure_notes.trim() !== initial.closure_notes.trim()) out.closure_notes = form.closure_notes.trim();
  return out;
}

/** The transition body for a status change, from the served requirements. Pure, for the tests. */
export function transitionBodyFor(form: EditForm, allowed: readonly AllowedIssueTransition[]): Record<string, unknown> {
  const reqs = requiredFor(allowed, form.status);
  const body: Record<string, unknown> = { status: form.status, closure_notes: form.closure_notes.trim() };
  if (reqs.has("resolution_code_id") || form.status === "resolved") {
    body.resolution_code_id = form.resolution_code_id || null;
    body.root_cause_code_id = form.root_cause_code_id || null;
  }
  if (reqs.has("dismissal_code_id") || form.status === "dismissed") body.dismissal_code_id = form.dismissal_code_id || null;
  return body;
}

/** Client-side check before Save, in the hub's own field names so the server's `missing_fields` lands in the same map. */
export function validateForSave(form: EditForm, initial: EditForm, allowed: readonly AllowedIssueTransition[]): Record<string, string> {
  const errs: Record<string, string> = {};
  if (!form.title.trim()) errs.title = "Title is required.";
  if (form.status !== initial.status) {
    for (const f of requiredFor(allowed, form.status)) {
      const v = (form as unknown as Record<string, string>)[f] ?? "";
      if (!v.trim()) errs[f] = `${MISSING_FIELD_LABEL[f] ?? f} is required.`;
    }
  }
  return errs;
}

/** The times line (spec §3.5). Pure, for the tests. */
export function timesLine(x: Issue): string {
  const parts = [`Raised ${fmtDay(x.created_at)}${x.raised_by ? ` by ${x.raised_by.name}` : ""}`];
  if (x.status === "open") parts.push(`${x.age_days} ${x.age_days === 1 ? "day" : "days"} open`);
  if (x.resolved) parts.push(`Resolved ${fmtDay(x.resolved.at)}`);
  if (x.closed) parts.push(`Closed ${fmtDay(x.closed.at)}`);
  if (x.dismissed) parts.push(`Dismissed ${fmtDay(x.dismissed.at)}`);
  return parts.join(" · ");
}

type Tab = "activity" | "action_items";

export function TenantIssueDetail(props: { entitlement?: string } = {}) {
  const { id = "" } = useParams<{ id: string }>();
  const toast = useToast();
  const { confirmWithReason } = useConfirm();
  const isAdmin = props.entitlement === "admin";

  const [issue, setIssue] = useState<Issue | null>(null);
  const [activity, setActivity] = useState<IssueActivityRow[]>([]);
  const [comments, setComments] = useState<IssueComment[]>([]);
  const [candidates, setCandidates] = useState<MemberCandidate[]>([]);
  const [resolutions, setResolutions] = useState<Code[]>([]);
  const [rootCauses, setRootCauses] = useState<Code[]>([]);
  const [dismissals, setDismissals] = useState<Code[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [watchOperations, setWatchOperations] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("activity");
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const users = useMemo(() => candidates.filter((c) => c.kind === "user"), [candidates]);
  const groups = useMemo(() => candidates.filter((c) => c.kind === "group"), [candidates]);

  const apply = useCallback((x: Issue) => {
    setIssue(x);
    setEditForm(issueToForm(x));
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [detail, act, cmt, dir, res, roots, dism, me, watches] = await Promise.all([
        apiFetch<{ issue: Issue }>(`/api/tenant/issues/${encodeURIComponent(id)}`),
        apiFetch<{ activity: IssueActivityRow[] }>(`/api/tenant/issues/${encodeURIComponent(id)}/activity?limit=100`),
        apiFetch<{ comments: IssueComment[] }>(`/api/tenant/issues/${encodeURIComponent(id)}/comments?limit=100`),
        apiFetch<{ candidates: MemberCandidate[] }>("/api/tenant/rasci-candidates").catch(() => ({ candidates: [] as MemberCandidate[] })),
        apiFetch<{ codes: Code[] }>("/api/tenant/action-item-codes/resolution-codes").catch(() => ({ codes: [] as Code[] })),
        apiFetch<{ codes: Code[] }>("/api/tenant/action-item-codes/root-cause-codes").catch(() => ({ codes: [] as Code[] })),
        apiFetch<{ codes: Code[] }>("/api/tenant/action-item-codes/dismissal-codes").catch(() => ({ codes: [] as Code[] })),
        apiFetch<{ user: { id: string } }>("/api/tenant/me").catch(() => null),
        apiFetch<{ operations: Array<{ id: string; name: string }> }>("/api/tenant/issues/watch-operations").catch(() => ({ operations: [] as Array<{ id: string; name: string }> })),
      ]);
      setWatchOperations(watches.operations);
      setCandidates(dir.candidates);
      apply(detail.issue);
      setActivity(act.activity);
      setComments(cmt.comments);
      setResolutions(res.codes);
      setRootCauses(roots.codes);
      setDismissals(dism.codes);
      if (me) setCurrentUserId(me.user.id);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [id, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const initialForm = useMemo(() => (issue ? issueToForm(issue) : null), [issue]);

  const saveInFlightRef = useRef(false);

  async function refreshTrail(issueId: string) {
    const [act, cmt] = await Promise.all([
      apiFetch<{ activity: IssueActivityRow[] }>(`/api/tenant/issues/${encodeURIComponent(issueId)}/activity?limit=100`),
      apiFetch<{ comments: IssueComment[] }>(`/api/tenant/issues/${encodeURIComponent(issueId)}/comments?limit=100`),
    ]);
    setActivity(act.activity);
    setComments(cmt.comments);
  }

  async function saveAll() {
    if (!issue || !editForm || !initialForm || saveInFlightRef.current) return;
    const errs = validateForSave(editForm, initialForm, issue.allowed_transitions);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setSaveError("Please fix the highlighted fields.");
      return;
    }
    const patchBody = patchBodyFor(editForm, initialForm);
    const statusChanged = editForm.status !== initialForm.status;
    const assigneeChanged = editForm.assignee_account_id !== initialForm.assignee_account_id;
    if (Object.keys(patchBody).length === 0 && !statusChanged && !assigneeChanged) return;
    saveInFlightRef.current = true;
    setSaveBusy(true);
    setSaveError(null);
    setFieldErrors({});
    try {
      const base = `/api/tenant/issues/${encodeURIComponent(issue.id)}`;
      const calls: Array<Promise<{ issue: Issue }>> = [];
      if (Object.keys(patchBody).length > 0) calls.push(apiFetch<{ issue: Issue }>(base, { method: "PATCH", body: patchBody }));
      if (assigneeChanged) calls.push(apiFetch<{ issue: Issue }>(`${base}/assignee`, { method: "PUT", body: { assignee_account_id: editForm.assignee_account_id || null } }));
      if (statusChanged) calls.push(apiFetch<{ issue: Issue }>(`${base}/transition`, { method: "POST", body: transitionBodyFor(editForm, issue.allowed_transitions) }));
      const results = await Promise.all(calls);
      apply(results[results.length - 1]!.issue);
      await refreshTrail(issue.id);
      toast.push({ kind: "success", message: "Saved." });
    } catch (e) {
      const serverFields = e instanceof ApiRequestError && Array.isArray(e.data?.missing_fields) ? (e.data.missing_fields as string[]) : null;
      if (serverFields) {
        const serverErrs: Record<string, string> = {};
        for (const f of serverFields) serverErrs[f] = `${MISSING_FIELD_LABEL[f] ?? f} is required.`;
        setFieldErrors(serverErrs);
        setSaveError("Please fix the highlighted fields.");
      } else {
        const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
        setSaveError(msg);
        toast.push({ kind: "error", message: msg });
      }
    } finally {
      saveInFlightRef.current = false;
      setSaveBusy(false);
    }
  }

  function cancelEdits() {
    if (initialForm) setEditForm(initialForm);
    setFieldErrors({});
    setSaveError(null);
  }

  async function addComment(body: string) {
    if (!issue) return;
    await apiFetch(`/api/tenant/issues/${encodeURIComponent(issue.id)}/comments`, { method: "POST", body: { body } });
    await refreshTrail(issue.id);
  }
  async function editCommentBody(commentId: string, body: string) {
    if (!issue) return;
    await apiFetch(`/api/tenant/issues/${encodeURIComponent(issue.id)}/comments/${encodeURIComponent(commentId)}`, { method: "PATCH", body: { body } });
    await refreshTrail(issue.id);
  }

  async function redact() {
    if (!issue) return;
    const reason = await confirmWithReason({
      title: `Redact the issue “${issue.title}”?`,
      detail: "Its title and description are replaced by “Redacted” on every surface. The codes, notes, activity and comments stay. This cannot be undone.",
      confirmLabel: "Redact",
      destructive: true,
      reasonField: { label: "Reason", placeholder: "e.g. names a customer contact" },
    });
    if (reason === null) return;
    try {
      const r = await apiFetch<{ issue: Issue }>(`/api/tenant/issues/${encodeURIComponent(issue.id)}/redact`, { method: "POST", body: { reason } });
      apply(r.issue);
      await refreshTrail(issue.id);
      toast.push({ kind: "success", message: "Issue redacted." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  return (
    <PageLayout title={issue?.title ?? "Issue"}>
      {issue ? (
        <div style={{ marginBottom: 12 }}>
          <Link to={`/app/initiatives/${issue.initiative.id}`} className="muted" style={{ fontSize: 13 }}>
            ← Back to {initiativeLabel(issue.initiative)}
          </Link>
        </div>
      ) : null}
      {error && <ErrorState detail={error} />}
      {loading && !error && <LoadingState />}
      {!loading && !error && issue && editForm && initialForm && (() => {
        const transitioning = editForm.status !== initialForm.status;
        const reqs = transitioning ? requiredFor(issue.allowed_transitions, editForm.status) : new Set<string>();
        const terminal = issue.allowed_transitions.length === 0;
        // Editable while picking a target, or after a move (the hub holds who: owners, admins, the assignee).
        const notesEditable = transitioning || issue.status !== "open";
        const locked = !!issue.redacted;
        const canEdit = issue.can_edit && !locked;
        function patch(updates: Partial<EditForm>) {
          setEditForm((f) => (f ? { ...f, ...updates } : f));
        }
        const hasPendingChanges = JSON.stringify(editForm) !== JSON.stringify(initialForm);
        return (
          <div style={{ display: "grid", gap: 12 }}>
            <div className="aiDetailMetaPills">
              <span className="pill">
                <strong>Initiative</strong> <Link to={`/app/initiatives/${issue.initiative.id}`}>{initiativeLabel(issue.initiative)}</Link>
              </span>
              <span className={statusPillClass(issue.status)}>{STATUS_WORDS[issue.status]}</span>
            </div>
            <div className="aiDetailMetaTimes">{timesLine(issue)}</div>

            <div className="card" style={{ display: "grid", gap: 12 }}>
              <label className="field">
                <span className="label">Title *</span>
                <input className="input" value={editForm.title} maxLength={200} disabled={!canEdit} onChange={(e) => patch({ title: e.target.value })} />
                {fieldErrors.title && <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.title}</div>}
              </label>
              <label className="field">
                <span className="label">Description</span>
                <textarea className="input" rows={4} value={editForm.description} maxLength={10000} disabled={!canEdit} onChange={(e) => patch({ description: e.target.value })} placeholder="What you know about it" />
              </label>

              <div className="aiDetailControlRow">
                <label className="field">
                  <span className="label">Assigned to</span>
                  <select className="select" value={editForm.assignee_account_id} disabled={!issue.can_assign || locked} onChange={(e) => patch({ assignee_account_id: e.target.value })}>
                    <option value="">Unassigned</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.display_name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="label">Status</span>
                  <select className="select" value={editForm.status} disabled={terminal || locked} onChange={(e) => patch({ status: e.target.value as IssueStatus })}>
                    {statusOptions(initialForm.status, issue.allowed_transitions).map((o) => (
                      <option key={o.value} value={o.value} disabled={o.disabled}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="label">Team</span>
                  <select className="select" value={editForm.team_group_id} disabled={!canEdit} onChange={(e) => patch({ team_group_id: e.target.value })}>
                    <option value="">None</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.display_name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="label">Held by watch</span>
                  {/* The tenant's watch-bearing operations the viewer can see; what is set stays listed even when the viewer cannot open it. */}
                  <select className="select" value={editForm.held_by_operation_id} disabled={!canEdit} onChange={(e) => patch({ held_by_operation_id: e.target.value })}>
                    <option value="">None</option>
                    {watchOperations.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                    {issue.held_by && !watchOperations.some((o) => o.id === issue.held_by!.operation_id) ? <option value={issue.held_by.operation_id}>{issue.held_by.name}</option> : null}
                  </select>
                </label>
                <label className="field">
                  <span className="label">Initiative</span>
                  <input className="input" value={initiativeLabel(issue.initiative)} readOnly />
                </label>
              </div>

              {editForm.status === "resolved" && (
                <div className="aiDetailCodesRow">
                  <label className="field">
                    <span className="label">Resolution code{reqs.has("resolution_code_id") ? " *" : ""}</span>
                    <select className="select" value={editForm.resolution_code_id} disabled={!transitioning} onChange={(e) => patch({ resolution_code_id: e.target.value })}>
                      <option value="">Select…</option>
                      {resolutions.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                      {!transitioning && issue.resolution_code_id && !resolutions.some((c) => c.id === issue.resolution_code_id) ? <option value={issue.resolution_code_id}>{issue.resolution_code_label}</option> : null}
                    </select>
                    <div className="fieldErrorSlot">{fieldErrors.resolution_code_id ?? ""}</div>
                  </label>
                  <label className="field">
                    <span className="label">Root cause code</span>
                    <select className="select" value={editForm.root_cause_code_id} disabled={!transitioning} onChange={(e) => patch({ root_cause_code_id: e.target.value })}>
                      <option value="">Select…</option>
                      {rootCauses.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                      {!transitioning && issue.root_cause_code_id && !rootCauses.some((c) => c.id === issue.root_cause_code_id) ? <option value={issue.root_cause_code_id}>{issue.root_cause_code_label}</option> : null}
                    </select>
                    <div className="fieldErrorSlot">{fieldErrors.root_cause_code_id ?? ""}</div>
                  </label>
                </div>
              )}
              {editForm.status === "closed" && issue.resolution_code_label && !transitioning && (
                <div className="aiDetailCodesRow">
                  <label className="field">
                    <span className="label">Resolution code</span>
                    <input className="input" value={issue.resolution_code_label} readOnly />
                  </label>
                  {issue.root_cause_code_label ? (
                    <label className="field">
                      <span className="label">Root cause code</span>
                      <input className="input" value={issue.root_cause_code_label} readOnly />
                    </label>
                  ) : null}
                </div>
              )}
              {editForm.status === "dismissed" && (
                <div className="aiDetailCodesRow">
                  <label className="field">
                    <span className="label">Dismissal code{reqs.has("dismissal_code_id") ? " *" : ""}</span>
                    <select className="select" value={editForm.dismissal_code_id} disabled={!transitioning} onChange={(e) => patch({ dismissal_code_id: e.target.value })}>
                      <option value="">Select…</option>
                      {dismissals.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                      {!transitioning && issue.dismissal_code_id && !dismissals.some((c) => c.id === issue.dismissal_code_id) ? <option value={issue.dismissal_code_id}>{issue.dismissal_code_label}</option> : null}
                    </select>
                    <div className="fieldErrorSlot">{fieldErrors.dismissal_code_id ?? ""}</div>
                  </label>
                </div>
              )}

              <label className="field">
                <span className="label">Closure notes{transitioning ? " *" : ""}</span>
                <textarea
                  className="input"
                  rows={3}
                  value={editForm.closure_notes}
                  readOnly={!notesEditable || locked}
                  maxLength={4000}
                  onChange={(e) => patch({ closure_notes: e.target.value })}
                  placeholder={transitioning ? (editForm.status === "open" ? "Why it is back" : "Why — what was done, or why it is dismissed") : "Required when resolving, closing, dismissing or reopening"}
                />
                {fieldErrors.closure_notes && <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.closure_notes}</div>}
                {!transitioning && issue.status !== "open" && !locked && <div className="help">Edit notes: change the text and Save (owners, admins and the assignee).</div>}
              </label>

              {saveError && <div className="help" style={{ color: "var(--danger-text)" }}>{saveError}</div>}

              <div className="aiDetailFooter">
                <span className="muted" style={{ fontSize: 12 }}>
                  {issue.edited ? `Edited ${fmtDay(issue.edited.at)} by ${issue.edited.by}` : ""}
                  {isAdmin && !locked ? (
                    <>
                      {issue.edited ? " · " : ""}
                      <button type="button" className="adminListAction isDanger" onClick={() => void redact()}>
                        Redact
                      </button>
                    </>
                  ) : null}
                  {issue.redacted ? `Redacted by ${issue.redacted.by} on ${fmtDay(issue.redacted.at)}` : null}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button type="button" className="btn btnSm" disabled={!hasPendingChanges || saveBusy} onClick={cancelEdits}>
                    Cancel
                  </button>
                  <button type="button" className="btn btnSm btnPrimary" disabled={!hasPendingChanges || saveBusy} onClick={() => void saveAll()}>
                    {saveBusy ? "Saving…" : "Save"}
                  </button>
                </span>
              </div>
            </div>

            <div className="tabs" role="tablist">
              <button type="button" role="tab" className={`tab${tab === "activity" ? " tabActive" : ""}`} onClick={() => setTab("activity")}>
                Activity
              </button>
              <button type="button" role="tab" className={`tab${tab === "action_items" ? " tabActive" : ""}`} onClick={() => setTab("action_items")}>
                Action items
              </button>
            </div>

            {tab === "activity" && (
              <ActivityThread
                comments={comments}
                activity={activity}
                currentUserId={currentUserId}
                formatRow={(a) => ({ actor: "", text: a.text, note: a.note })}
                commentAuthor={(c) => ({ id: c.author_account_id, label: c.author.name })}
                onAdd={addComment}
                onEdit={editCommentBody}
              />
            )}
            {tab === "action_items" && <ActionItemsTab issue={issue} />}
          </div>
        );
      })()}
    </PageLayout>
  );
}

export const NO_ACTION_ITEMS_TEXT = "No action items yet.";

/** The Action items tab (spec §3.5): the rows joined by source_ref, and the create link carrying the issue in location state. Pure, for the tests. */
export function ActionItemsTab(props: { issue: Issue }): JSX.Element {
  const x = props.issue;
  const open = x.status === "open" || x.status === "resolved";
  return (
    <div className="card adminList">
      <div className="toolbar" style={{ marginBottom: 10 }}>
        <div className="toolbarGroup" style={{ marginLeft: "auto" }}>
          {open ? (
            <Link className="btn btnSm btnPrimary" to="/app/action-items/new" state={{ source_ref: { initiative_id: x.initiative.id, initiative_issue_id: x.id }, title: x.title, initiative_name: x.initiative.name }}>
              + New action item
            </Link>
          ) : (
            <span className="muted" style={{ fontSize: 12 }}>
              An action item is created against an open or resolved issue.
            </span>
          )}
        </div>
      </div>
      {x.action_item_rows.length === 0 ? (
        <div className="adminListEmpty">{NO_ACTION_ITEMS_TEXT}</div>
      ) : (
        <div className="tableWrap">
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Assigned to</th>
                <th>Due</th>
                <th>Age</th>
              </tr>
            </thead>
            <tbody>
              {x.action_item_rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link className="adminListName" to={`/app/action-items/${r.id}`}>
                      {r.title}
                    </Link>
                  </td>
                  <td>
                    <span className={`pill ${r.status === "resolved" ? "pillOk" : r.status === "dismissed" ? "pillNeutral" : "pillWarn"}`}>{r.status.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())}</span>
                  </td>
                  <td>{r.assignee ?? <span className="muted">—</span>}</td>
                  <td>{r.due_at ? fmtDay(r.due_at) : <span className="muted">—</span>}</td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {r.age_days} {r.age_days === 1 ? "day" : "days"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

